import {
  createCustomClient,
  createSsrAuthMutation,
  createSsrAuthQuery,
  createSsrCache,
  createSsrMutation,
  createSsrOptionalAuthQuery,
  createSsrQuery,
  createUseAuthQueryHook,
  createUseMutationHook,
  createUsePaginatedAuthQueryHook,
  createUsePaginatedQueryHook,
  createUseQueryHook,
} from '@vgn-medien-holding/vgn-gql-shared-fe-queries';

const url = process.env.NEXT_PUBLIC_CMS_API_URL ?? '';
const apiToken = process.env.NEXT_PUBLIC_CMS_API_TOKEN ?? '';
const includeAuthExchange = true;

function createPostClient(ssrCache: ReturnType<typeof createSsrCache>) {
  const client = createCustomClient({ url, apiToken, ssrCache, includeAuthExchange });
  const createRequestOperation = client.createRequestOperation.bind(client);

  // The shared factory does not expose preferGetMethod. Set it at operation
  // creation so hooks, SSR helpers and direct client calls all use JSON POST.
  client.createRequestOperation = (kind, request, context) =>
    createRequestOperation(kind, request, { ...context, preferGetMethod: false });

  return client;
}

let ssrCache = createSsrCache();
let client = createPostClient(ssrCache);
let ssrQuery = createSsrQuery(client, apiToken);
let ssrMutation = createSsrMutation(client, apiToken);
let ssrAuthQuery = createSsrAuthQuery(client);
let ssrOptionalAuthQuery = createSsrOptionalAuthQuery(client);
let ssrAuthMutation = createSsrAuthMutation(client);

// Recreates the server-side urql client + ssrCache. Called from the NextAuth
// handler (same as woman/trend/news/autorevue) so authed customer queries never
// serve another user's / a stale session's cached result.
function clearCache() {
  ssrCache = createSsrCache();
  client = createPostClient(ssrCache);
  ssrQuery = createSsrQuery(client, apiToken);
  ssrMutation = createSsrMutation(client, apiToken);
  ssrAuthQuery = createSsrAuthQuery(client);
  ssrOptionalAuthQuery = createSsrOptionalAuthQuery(client);
  ssrAuthMutation = createSsrAuthMutation(client);
}

const useQuery = createUseQueryHook();
const useMutation = createUseMutationHook();
const useAuthQuery = createUseAuthQueryHook();
const usePaginatedQuery = createUsePaginatedQueryHook();
const usePaginatedAuthQuery = createUsePaginatedAuthQueryHook();

export function createUrqlClient() {
  const ssrCache = createSsrCache();
  const client = createPostClient(ssrCache);
  const ssrQuery = createSsrQuery(client, apiToken);
  const ssrMutation = createSsrMutation(client, apiToken);
  const ssrAuthQuery = createSsrAuthQuery(client);
  const ssrOptionalAuthQuery = createSsrOptionalAuthQuery(client);
  const ssrAuthMutation = createSsrAuthMutation(client);

  return {
    client,
    ssrCache,
    ssrQuery,
    ssrMutation,
    ssrAuthQuery,
    ssrAuthMutation,
    ssrOptionalAuthQuery,
  };
}

export {
  clearCache,
  client,
  ssrAuthMutation,
  ssrAuthQuery,
  ssrCache,
  ssrMutation,
  ssrOptionalAuthQuery,
  ssrQuery,
  useAuthQuery,
  useMutation,
  usePaginatedAuthQuery,
  usePaginatedQuery,
  useQuery,
};
